import { ArrowSquareOut, Wallet } from "@phosphor-icons/react";
import { Button } from "@replicate/ui";
import { match } from "ts-pattern";
import { formatCurrency } from "../intl_helpers";
import type { Account } from "../types";
import { route } from "../urls";
import GlyphLoader from "./glyph-loader";
import IconTooltip from "./icon-tooltip";
import { useInvoicesQuery, useUnusedCreditQuery } from "./invoices";

function ValueDisplay({
  title,
  meta,
  children,
  warn = false,
}: {
  title: string | React.ReactNode;
  meta?: React.ReactNode;
  children: React.ReactNode;
  warn?: boolean;
}) {
  return (
    <div
      className={`${
        warn ? "r8-banner r8-banner--warning gap-0" : ""
      } flex flex-col items-center justify-center flex-1 p-3`}
    >
      <div className="flex items-center justify-center gap-x-1 text-r8-sm flex-wrap">
        <div className="text-r8-gray-11 whitespace-nowrap">{title}</div>
        {meta}
      </div>
      <div className="flex items-center justify-center gap-1 flex-wrap">
        <div className="text-r8-2xl font-heading font-semibold">{children}</div>
      </div>
    </div>
  );
}

export default function BillingSummary({
  account,
  linkToInvoices = false,
  linkToAddCredit,
  requiresCredit = false,
}: {
  account: Account;
  linkToInvoices?: boolean;
  linkToAddCredit?: string;
  requiresCredit?: boolean;
}) {
  const invoiceQuery = useInvoicesQuery(account);
  const unusedCreditQuery = useUnusedCreditQuery(account);

  const latestInvoice = match(invoiceQuery)
    .with(
      { status: "success" },
      ({ data: { invoices } }) =>
        // Invoices are sorted in reverse chronological order.
        invoices.filter((invoice) => invoice.type === "monthly-usage")[0]
    )
    .otherwise(() => null);

  const currentInvoice =
    latestInvoice &&
    (!latestInvoice.ended_before ||
      Date.parse(latestInvoice.ended_before) > Date.now())
      ? latestInvoice
      : null;

  const outstandingBalance = Number.parseFloat(
    currentInvoice?.total_cost || "0"
  );

  const warning =
    outstandingBalance > 0 && requiresCredit
      ? `You have an overage charge, as your usage has exceeded the credit you purchased. If you do not purchase more credits, we will bill your default payment method for ${formatCurrency(
          outstandingBalance
        )} at the end of the month.`
      : "";

  return (
    <div className="flex flex-col sm:flex-row flex-wrap lg:flex-nowrap justify-between border border-b-2 border-r8-gray-6 rounded bg-white dark:bg-r8-gray-1 min-h-24 items-stretch lg:divide-x">
      {linkToInvoices ? (
        <div className="flex items-center gap-2 font-semibold text-r8-xl px-8 py-3 justify-center">
          <Wallet size={24} weight="bold" />
          Billing
        </div>
      ) : null}

      {match(invoiceQuery)
        .with({ status: "error" }, () => (
          <div className="flex items-center justify-center flex-1 p-3">
            <p>Unable to fetch usage</p>
          </div>
        ))
        .with({ status: "success" }, () => (
          <>
            <ValueDisplay
              title={
                warning ? (
                  <div className="flex items-center gap-1">
                    <p>Outstanding balance</p>
                    <IconTooltip
                      icon="Question"
                      weight="light"
                      tooltipText={warning}
                    />
                  </div>
                ) : (
                  "Outstanding balance"
                )
              }
              warn={warning !== ""}
            >
              {formatCurrency(outstandingBalance)}
            </ValueDisplay>
            <ValueDisplay title="Usage this month">
              {formatCurrency(
                Number.parseFloat(
                  currentInvoice?.total_cost_before_adjustments || "0"
                )
              )}
            </ValueDisplay>
          </>
        ))
        .with({ status: "pending" }, () => (
          <div className="flex items-center justify-center flex-1 p-3 gap-3">
            <GlyphLoader />
            <p>Loading usage...</p>
          </div>
        ))
        .exhaustive()}

      {linkToAddCredit
        ? match(unusedCreditQuery)
            .with({ status: "error" }, () => (
              <div className="flex items-center justify-center flex-1 p-3">
                <p>Unable to fetch credit</p>
              </div>
            ))
            .with({ status: "success" }, ({ data: { unused_credit } }) => {
              const unusedCredit = Number.parseFloat(unused_credit || "0");
              return (
                <ValueDisplay
                  title="Credit"
                  meta={
                    <span>
                      {linkToAddCredit && unusedCredit <= 0 ? (
                        <a href={linkToAddCredit}>(Add more)</a>
                      ) : null}
                    </span>
                  }
                >
                  {formatCurrency(unusedCredit)}
                </ValueDisplay>
              );
            })
            .with({ status: "pending" }, () => (
              <div className="flex items-center justify-center flex-1 p-3 gap-3">
                <GlyphLoader />
                <p>Loading credit...</p>
              </div>
            ))
            .exhaustive()
        : match(unusedCreditQuery)
            .with({ status: "success" }, ({ data: { unused_credit } }) => {
              const unusedCredit = Number.parseFloat(unused_credit || "0");

              if (unusedCredit <= 0 && !linkToAddCredit) {
                return null;
              }

              return (
                <ValueDisplay title="Credit">
                  {formatCurrency(unusedCredit)}
                </ValueDisplay>
              );
            })
            .otherwise(() => null)}

      {linkToInvoices ? (
        <div className="flex flex-col gap-2 p-3 flex-1 justify-center md:max-w-64">
          <div className="flex items-stretch justify-stretch lg:justify-center [&>*]:w-full [&>*]:sm:flex-none">
            <Button
              size="sm"
              endIcon={<ArrowSquareOut />}
              disabled={!latestInvoice}
              render={
                latestInvoice?.id ? (
                  // biome-ignore lint/a11y/useAnchorContent: rendered by Button
                  <a
                    href={route("user_invoice_detail", {
                      username: account.username,
                      invoice_id: latestInvoice.id,
                    })}
                    target="_blank"
                    rel="noreferrer"
                  />
                ) : (
                  <div />
                )
              }
            >
              <span className="whitespace-nowrap">Latest invoice</span>
            </Button>
          </div>

          <div className="flex items-stretch justify-stretch lg:justify-center [&>*]:w-full [&>*]:sm:flex-none">
            <Button
              variant="outlined"
              size="sm"
              render={
                // biome-ignore lint/a11y/useAnchorContent: rendered by Button
                <a href={route("account_billing_settings")} />
              }
            >
              <span className="whitespace-nowrap">Manage billing</span>
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
